import Raven from 'raven-js'
import { put, take, takeLatest, all, fork, select, call } from 'redux-saga/effects'
import { updates,updateAt } from 'fn-update'
import { asynch } from 'redux-asynch-middleware'

import { urlParam } from '../../../util/url'
import { Machine } from '../../goose'
import { refreshToken, getUser, getUserConfiguration } from '../../../api'
import analytics from '../../../util/analytics'
import log from '../../../util/log'
import WebviewModule from '../webview'

/**
 * USAGE: This module figures out how it wants the app to authenticate, then expects the
 * associated module to emit a LOGIN action with a user/api_token.
**/

const ns = (action) => `auth/${action}`

const LOGIN = ns('LOGIN')
const TRY_TOKEN_REFRESH = asynch(ns('TRY_TOKEN_REFRESH'))
const SET_LOGIN_MECHANISM = ns('SET_LOGIN_MECHANISM')
const LOGIN_PROVIDER_REGISTERED = ns('LOGIN_PROVIDER_REGISTERED')
const REQUEST_LOGIN = ns('REQUEST_LOGIN')
const SET_CONFIGURATION = ns('SET_CONFIGURATION')
const GET_USER = asynch(ns('GET_USER'))

export const LOGIN_REQUESTED = ns('LOGIN_REQUESTED')

const setLoginMechanism = (mechanism) => ({
  type: SET_LOGIN_MECHANISM,
  payload: mechanism
})

export const login = ({ user, api_token }) => (
  { type: LOGIN, payload: { user, api_token }
})

export const registerAsLoginProvider = (key) => ({
  type: LOGIN_PROVIDER_REGISTERED, payload: { key }
})

const reducer = Machine({
  [Machine.INIT]: () => ({
    ready: false,
    cached: true,
    mechanism: null,
    user: null,
    api_token: null,
    configuration: null
  })
  ,[SET_LOGIN_MECHANISM]: (mechanism) => updateAt('mechanism', mechanism)
  ,[TRY_TOKEN_REFRESH.failure]: () => updates({ cached: false })
  ,[SET_CONFIGURATION]: (configuration) => updates({ configuration })
  ,[LOGIN]: (result) => updates({
    ready: true,
    user: result.user,
    api_token: result.api_token
  })
})

function* onSuccessfulLogin(action) {
  const payload = action.payload

  // Store the API token (if one exists) for the logged-in user for subsequent
  // authenticated requests.
  if (payload.api_token) {
    window.localStorage.setItem('authToken', payload.api_token)
  }

  // get user's configuration with the configuration endpoint
  try {
    const configuration = yield call(getUserConfiguration)
    yield put({ type: SET_CONFIGURATION, payload: configuration });
  } catch (error) {
    // do nothing for now, let the configuration empty
  }

  // Identify the user for analytics and error reporting.
  analytics.identify(payload.user.id, {
    "$last_login": new Date()
  })

  Raven.setUserContext({id: payload.user.id})
}

function* onInitialize() {
  // Check if an api token was given in the URL.
  const tokenOverride = yield select(
    state => urlParam('api_token', state.router.location.search)
  )

  // If so, override whatever token we have in local storage.
  if (tokenOverride) {
    window.localStorage.setItem('authToken', tokenOverride)
  }

  let authToken = window.localStorage.getItem('authToken')

  // Alex (11 June 2019): The code before was looking for `api_token` in `authToken`
  // and passed that to `refreshToken`, but it seems that it was always undefined, and
  // `refreshToken` does not actually need any parameters. Nevertheless, we're being
  // extra careful in case `authToken` has an `api_token` property.
  if (authToken && authToken.api_token) authToken = authToken.api_token

  if (authToken) {
    // NB! Temporary treatment of both api token types for backwards compatibility.
    if (authToken.length <= 40) {
      // Legacy API token. Attempt to refresh and fetch the user.
      yield put({ type: TRY_TOKEN_REFRESH, promise: refreshToken() })
    } else{
      // OAuth2 API token. Fetch the user.
      yield put({ type: GET_USER, promise: getUser() })
    }
  } else {
    // Otherwise, request a login.
    yield requestLogin()
  }
}

function* onUserFetched(action){
  log.info('Successfully fetched user!')
  // For backwards compatibility with old 'login' action.
  const authToken = window.localStorage.getItem('authToken')

  yield put(login({ user: action.result, api_token: authToken }))
}

function* onTokenRefreshed(action){
  log.info('Successful token refresh!')
  const { user, api_token } = action.result
  yield put(login({user,api_token}))
}

function* requestLogin() {
  // Cleanup storage.
  window.localStorage.removeItem('authToken')

  // Decide on which login mechanism we'll use and request login.
  yield pickLoginMechanism()
  yield put({ type: REQUEST_LOGIN })
}

function* pickLoginMechanism() {
  const isMessengerContext = yield select(
    WebviewModule.selectors.isMessengerContext
  )
  if (isMessengerContext) {
    yield put(setLoginMechanism('messenger'))
  } else if (process.env.NODE_ENV === 'development'){
    yield put(setLoginMechanism('dev'))
  } else{
    yield put(setLoginMechanism('basic'))
  }
}

function* waitForLoginRequestWhenReady(){
  while (true) {
    yield all([
      take(LOGIN_PROVIDER_REGISTERED),
      take(REQUEST_LOGIN)
    ])
    yield put({ type: LOGIN_REQUESTED })
  }
}

const saga = function*(){
  yield all([
    takeLatest(WebviewModule.actionTypes.SET_CONTEXT, onInitialize),
    takeLatest(TRY_TOKEN_REFRESH, onTokenRefreshed),
    takeLatest(TRY_TOKEN_REFRESH.failure, requestLogin),
    takeLatest(LOGIN, onSuccessfulLogin),
    takeLatest(GET_USER, onUserFetched),
    takeLatest(GET_USER.failure, requestLogin),
    fork(waitForLoginRequestWhenReady)
  ])
}

export default {
  key: 'auth',
  reducer,
  saga,
  dependencies: [
    WebviewModule
  ]
}



// WEBPACK FOOTER //
// ./src/redux/modules/auth/auth.js